import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Search, X, CornerDownLeft } from 'lucide-react';
import { useDebounce } from '../hooks/useDebounce';
import apiClient from '../config/axios';
import { getActiveSystem } from '../config/system';
import { useSystem } from '../context/SystemContext';
import { useAuth } from '../context/AuthContext';
import { entityTypeLabel, entityTypePath } from '../utils/entityTypes';
import previewText from '../utils/suggestionPreview';
import entityTypeIcon from '../utils/entityIcons';
import { buildCommands, filterCommands } from '../utils/paletteCommands';
import { track } from '../utils/analytics';

// Command palette (issue 198) — Cmd/Ctrl-K anywhere in the app.
//
// One dialog over two row sources: COMMANDS (navigation targets derived from
// the Family registry via `paletteCommands`, plus the global actions) filtered
// locally with no network, and CONTENT (the same `/api/search` omnisearch the
// header box uses, debounced 300ms, per-type limit 5, rendered with the same
// preview lines and type icons — one rendering of a result row, not two).
//
// The binding is MODIFIER-GATED on purpose: Cmd/Ctrl-K opens it even while
// focus sits in an input (the conventional palette behaviour), and a bare "k"
// typed anywhere never does — so it cannot fight the header search or the
// authoring screens' own inputs. The broader single-key shortcut scheme the
// issue sketches ("/", "g" chords, "?") is deferred, stated on the issue.
//
// Accessibility is most of the feature: role="dialog" aria-modal with an
// accessible name, focus moves to the input on open and RETURNS to the opener
// on close, Tab is trapped inside the dialog, the input is a combobox with
// aria-activedescendant over one flat listbox, arrows move, Enter runs,
// Escape closes.
const PER_TYPE_LIMIT = 5;

const CommandPalette = ({ isDarkMode = true, onToggleTheme = null }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { systems, setSystem } = useSystem();
  const { isAuthenticated } = useAuth();

  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [contentResults, setContentResults] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const dialogRef = useRef(null);
  const inputRef = useRef(null);
  const openerRef = useRef(null);
  const abortRef = useRef(null);

  const debouncedQuery = useDebounce(query, 300);

  const close = useCallback(() => {
    setIsOpen(false);
    setQuery('');
    setContentResults([]);
    setActiveIndex(0);
    if (abortRef.current) abortRef.current.abort();
    // Return focus to wherever Cmd/Ctrl-K was pressed.
    if (openerRef.current && typeof openerRef.current.focus === 'function') {
      openerRef.current.focus();
    }
    openerRef.current = null;
  }, []);

  const open = useCallback(() => {
    openerRef.current = document.activeElement;
    setIsOpen(true);
    setActiveIndex(0);
    track('command-palette-open');
  }, []);

  // The global binding. Listening while closed is what makes the palette
  // available on every page without each page knowing about it. A ref mirrors
  // isOpen so the one document listener toggles without re-subscribing.
  const isOpenRef = useRef(false);
  useEffect(() => { isOpenRef.current = isOpen; }, [isOpen]);
  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey
          && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        if (isOpenRef.current) close();
        else open();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [close, open]);

  // Focus the input on open.
  useEffect(() => {
    if (isOpen && inputRef.current) inputRef.current.focus();
  }, [isOpen]);

  // Content search — the header omnisearch's exact discipline: 300ms debounce,
  // 2-char minimum, abort the in-flight request on every new one.
  useEffect(() => {
    if (!isOpen) return undefined;
    const q = debouncedQuery.trim();
    if (q.length < 2) {
      setContentResults([]);
      return undefined;
    }
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    apiClient.get(
      `/api/search?q=${encodeURIComponent(q)}&limit=${PER_TYPE_LIMIT}`,
      { signal: controller.signal }
    )
      .then((res) => {
        setContentResults(res.data.results || []);
        setActiveIndex(0);
      })
      .catch((err) => {
        if (err.name !== 'CanceledError' && err.code !== 'ERR_CANCELED') {
          console.error('Command palette search error:', err);
        }
      });
    return () => controller.abort();
  }, [debouncedQuery, isOpen]);

  const commands = useMemo(() => {
    if (!isOpen) return [];
    return filterCommands(
      buildCommands({
        systemSlug: getActiveSystem(),
        systems,
        isAuthenticated,
        isDarkMode,
        onToggleTheme,
        onSwitchSystem: setSystem,
      }),
      query
    );
  }, [isOpen, systems, isAuthenticated, isDarkMode, onToggleTheme, setSystem, query]);

  // One flat row list for keyboard navigation: commands first, content after.
  const rows = useMemo(() => ([
    ...commands.map((c) => ({ kind: 'command', command: c })),
    ...contentResults.map((r) => ({ kind: 'content', result: r })),
  ]), [commands, contentResults]);

  // A shrinking row list must not strand the highlight past the end.
  useEffect(() => {
    if (activeIndex >= rows.length && activeIndex !== 0) setActiveIndex(0);
  }, [rows.length, activeIndex]);

  // The highlighted row stays in sight as the arrow keys move it (issue 1464): the list scrolls to it, no further.
  useEffect(() => {
    document.getElementById(`palette-option-${activeIndex}`)?.scrollIntoView?.({ block: 'nearest' });
  }, [activeIndex]);

  const runRow = useCallback((row) => {
    const from = `${location.pathname}${location.search}`;
    if (row.kind === 'command') {
      const cmd = row.command;
      track('command-palette-run', { command: cmd.id });
      close();
      if (cmd.href) navigate(cmd.href, { state: { from } });
      else if (cmd.run) cmd.run();
    } else {
      const item = row.result;
      track('command-palette-run', { command: 'content', entity_type: item.entity_type });
      close();
      navigate(`${entityTypePath(item.entity_type)}?slug=${item.slug}`, { state: { from } });
    }
  }, [close, navigate, location.pathname, location.search]);

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      close();
      return;
    }
    if (e.key === 'Tab') {
      // Focus trap: keep Tab inside the dialog (input + close button).
      const focusables = dialogRef.current
        ? Array.from(dialogRef.current.querySelectorAll('input, button'))
        : [];
      if (focusables.length > 0) {
        e.preventDefault();
        const idx = focusables.indexOf(document.activeElement);
        const next = e.shiftKey
          ? (idx <= 0 ? focusables.length - 1 : idx - 1)
          : (idx >= focusables.length - 1 ? 0 : idx + 1);
        focusables[next].focus();
      }
      return;
    }
    if (rows.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((prev) => (prev < rows.length - 1 ? prev + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((prev) => (prev > 0 ? prev - 1 : rows.length - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (rows[activeIndex]) runRow(rows[activeIndex]);
    }
  };

  if (!isOpen) return null;

  // Section header before a row: command sections by their `section`, content
  // rows by entity type — the header omnisearch's own grouping.
  const headerBefore = (row, index) => {
    if (row.kind === 'command') {
      const prev = index > 0 ? rows[index - 1] : null;
      if (!prev || prev.kind !== 'command' || prev.command.section !== row.command.section) {
        return row.command.section;
      }
      return null;
    }
    const prev = index > 0 ? rows[index - 1] : null;
    if (!prev || prev.kind !== 'content' || prev.result.entity_type !== row.result.entity_type) {
      return entityTypeLabel(row.result.entity_type);
    }
    return null;
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch justify-center bg-black/60 sm:items-start sm:p-4 sm:pt-[12vh]"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      {/* Full-screen sheet on phones; floating panel from sm up. */}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        className="card flex w-full flex-col overflow-hidden p-0 sm:h-auto sm:max-h-[70vh] sm:max-w-xl sm:rounded"
        onKeyDown={handleKeyDown}
      >
        <div
          className="flex items-center gap-2 px-3 py-2"
          style={{ borderBottom: '1px solid var(--chrome-border)' }}
        >
          <Search className="h-4 w-4 flex-shrink-0" style={{ color: 'var(--text-muted)' }} aria-hidden="true" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setActiveIndex(0); }}
            placeholder="Type a command or search…"
            className="min-w-0 flex-1 bg-transparent py-1 text-sm outline-none"
            style={{ color: 'var(--text-primary)' }}
            role="combobox"
            aria-label="Command palette input"
            aria-controls="command-palette-listbox"
            aria-expanded={rows.length > 0}
            aria-autocomplete="list"
            aria-activedescendant={rows.length > 0 ? `palette-option-${activeIndex}` : undefined}
            autoComplete="off"
          />
          <button
            type="button"
            onClick={close}
            className="p-1"
            aria-label="Close command palette"
          >
            <X className="h-4 w-4" style={{ color: 'var(--text-muted)' }} />
          </button>
        </div>

        <ul
          id="command-palette-listbox"
          role="listbox"
          aria-label="Commands and results"
          className="flex-1 overflow-y-auto py-1"
        >
          {rows.length === 0 && (
            <li className="px-4 py-6 text-center text-sm" style={{ color: 'var(--text-muted)' }} aria-hidden="true">
              {query.trim().length >= 2 ? 'No commands or results match.' : 'Type to search commands and content.'}
            </li>
          )}
          {rows.map((row, index) => {
            const header = headerBefore(row, index);
            const isCommand = row.kind === 'command';
            const Icon = isCommand ? row.command.icon : entityTypeIcon(row.result.entity_type);
            const label = isCommand ? row.command.label : row.result.name;
            const preview = isCommand ? null : previewText(row.result.entity_type, row.result.preview);
            const key = isCommand ? row.command.id : `${row.result.entity_type}:${row.result.slug}`;
            return (
              <React.Fragment key={key}>
                {header && (
                  <li
                    className="px-3 pt-2 pb-1 text-xs font-semibold uppercase tracking-wider"
                    style={{ color: 'var(--text-muted)' }}
                    aria-hidden="true"
                  >
                    {header}
                  </li>
                )}
                <li
                  id={`palette-option-${index}`}
                  role="option"
                  aria-selected={index === activeIndex}
                  className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm transition-colors duration-100"
                  style={
                    index === activeIndex
                      ? { background: 'var(--gold-dim)', color: 'var(--gold)' }
                      : { color: 'var(--text-primary)' }
                  }
                  onMouseDown={(e) => { e.preventDefault(); runRow(row); }}
                  onMouseEnter={() => setActiveIndex(index)}
                >
                  {Icon && (
                    <Icon className="h-4 w-4 flex-shrink-0" style={{ color: 'var(--text-muted)' }} aria-hidden="true" />
                  )}
                  <span className="min-w-0 flex-1">
                    <span className="block truncate">{label}</span>
                    {preview && (
                      <span className="block truncate text-xs" style={{ color: 'var(--text-muted)' }}>
                        {preview}
                      </span>
                    )}
                  </span>
                  {index === activeIndex && (
                    <CornerDownLeft className="h-3.5 w-3.5 flex-shrink-0" style={{ color: 'var(--text-muted)' }} aria-hidden="true" />
                  )}
                </li>
              </React.Fragment>
            );
          })}
        </ul>

        <div
          className="hidden items-center gap-3 px-3 py-1.5 text-xs sm:flex"
          style={{ borderTop: '1px solid var(--chrome-border)', color: 'var(--text-muted)' }}
          aria-hidden="true"
        >
          <span>↑↓ navigate</span>
          <span>↵ select</span>
          <span>esc close</span>
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;
